import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTRPC } from "../../lib/api";
import { ErrorText } from "../../ui";
import { safeHref } from "../chat/markdown";
import { dayWindow, formatMeetingTime, meetErrorText, type Meeting } from "./model";
import { hasNotesView } from "./notes-model";

/** The dashboard's "Meetings" part of Today: this workspace's meetings starting today (local time). */
export function TodayMeetings({ workspaceId, now }: { workspaceId: string; now: Date }) {
  const trpc = useTRPC();
  // Midnight to midnight: the same values all day, so the query key only changes with the day.
  const { from, to } = dayWindow(now);
  const meetings = useQuery(trpc.meet.list.queryOptions({ workspaceId, from, to }));

  if (meetings.isPending) return <p className="text-sm text-muted">Loading meetings…</p>;
  if (meetings.isError) return <ErrorText>Meetings could not be loaded: {meetErrorText(meetings.error)}</ErrorText>;
  if (meetings.data.length === 0) return <p className="text-sm text-muted">No meetings today.</p>;
  return <MeetingList workspaceId={workspaceId} meetings={meetings.data} />;
}

const NOTES_LABEL: Partial<Record<Meeting["notes"]["status"], string>> = { ready: "Notes", pending: "Notes pending" };

export function MeetingList({ workspaceId, meetings }: { workspaceId: string; meetings: readonly Meeting[] }) {
  return (
    <ul aria-label="Today’s meetings" className="flex flex-col gap-1">
      {meetings.map((m) => {
        const href = safeHref(m.meetUri);
        return (
          <li key={m.id} className="flex items-center gap-3 text-sm">
            <span className="w-28 shrink-0 tabular-nums text-muted">{formatMeetingTime(m)}</span>
            {/* w-0: the title must not widen the dashboard's column on a phone; it takes what is left. */}
            <span className="w-0 min-w-0 flex-1 truncate text-text">{m.title}</span>
            {hasNotesView(m.notes) ? (
              <Link
                to="/w/$workspaceId/meetings/$meetingId"
                params={{ workspaceId, meetingId: m.id }}
                className="text-accent underline-offset-2 hover:underline"
                aria-label={`Notes for ${m.title}`}
              >
                {NOTES_LABEL[m.notes.status] ?? "Notes"}
              </Link>
            ) : null}
            {href === null ? null : (
              <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent underline-offset-2 hover:underline">
                Join
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
}
